/* eslint react-hooks/exhaustive-deps: "warn" */
import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './SalesProspectingLeadGenerationSignals.module.scss';

type Translate = (key: string) => string;

type Signal = {
  type: 'entity' | 'capital' | 'people' | 'financials' | 'identity' | 'footprint';
  title: string;
  description: string;
};

const getSignals = (t: Translate): Signal[] => [
  {
    type: 'entity',
    title: t('New entity'),
    description: t(
      'A prospect incorporates a subsidiary. New market, new budget, new decision-maker, and nobody has called them yet.',
    ),
  },
  {
    type: 'capital',
    title: t('Capital change'),
    description: t(
      'Share capital increased or new investment registered. Their spending capacity changed this week, not last quarter.',
    ),
  },
  {
    type: 'people',
    title: t('People change'),
    description: t(
      'A director or officer is appointed. The buying committee just reset, and incumbent vendors lose their champion.',
    ),
  },
  {
    type: 'financials',
    title: t('Financials filed'),
    description: t(
      'Fresh accounts cross a revenue or headcount threshold. The account moves into your ICP on the day it files.',
    ),
  },
  {
    type: 'identity',
    title: t('Identity change'),
    description: t(
      'Registered name or legal form changes. Your CRM record is now wrong and your emails are going to a company that no longer answers to that name.',
    ),
  },
  {
    type: 'footprint',
    title: t('Footprint change'),
    description: t(
      'Registered office moves, or a branch opens in a new country. Territory ownership and coverage change with it.',
    ),
  },
];

export const SalesProspectingLeadGenerationSignals = () => {
  const { t } = useInt();
  const signals = getSignals(t);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.marker}>
          <span className={styles.markerNum}>06</span> / {t('Signals')}
        </div>

        <div className={styles.body}>
          <div className={styles.head}>
            <TitleBlock
              align="left"
              title={t('Buying signals, taken from the register')}
              description={t(
                'Every filed event carries a date and a source registry. These are not inferred intent scores built from browsing behaviour. Subscribe a segment and each change on it arrives daily, tagged by signal type and routed to the place your team works.',
              )}
            />
          </div>

          <div className={styles.grid}>
            {signals.map((signal) => (
              <div key={signal.type} className={styles.item}>
                <h4 className={styles.itemTitle}>
                  <span className={cn(styles.tag, styles[signal.type])}>{t(signal.type)}</span>
                  {signal.title}
                </h4>

                <p className={styles.itemText}>{signal.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
